import React, { useEffect, useState } from "react";
import { Activity, Play, Loader2, AlertTriangle, CheckCircle2 } from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, Legend,
  ResponsiveContainer, CartesianGrid,
} from "recharts";
import { toast } from "sonner";
import api, { formatErr } from "../lib/api";

/**
 * Wave BL — Dynamic Simulation Tab.
 *
 * Lets the engineer pick a pre-built scenario (power failure, CW failure,
 * feed trip, startup, turndown) and run it against the converged steady
 * state. Shows time-series charts of vessel pressure + temperature +
 * level for every vessel in the flowsheet.
 */
export default function DynamicSimulationTab({ projectId, runId }) {
  const [scenarios, setScenarios] = useState([]);
  const [scenario, setScenario] = useState("total_power_failure");
  const [tEnd, setTEnd] = useState(3600);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get("/sim/dynamic/scenarios")
      .then((r) => setScenarios(r.data?.scenarios || []))
      .catch((e) => setErr(formatErr(e.response?.data?.detail) || e.message));
  }, []);

  const run = async () => {
    if (!projectId) { toast.error("No project selected"); return; }
    setRunning(true); setErr(""); setResult(null);
    try {
      const r = await api.post(`/sim/dynamic/run/${projectId}`, {
        scenario, t_end_s: tEnd,
      });
      setResult(r.data);
      if (!r.data?.ok) setErr(r.data?.reason || "Scenario run failed");
      else toast.success(`Scenario "${scenario}" completed (${r.data.n_steps} steps, ${r.data.n_vessels} vessels)`);
    } catch (e) {
      const detail = formatErr(e.response?.data?.detail) || e.message;
      setErr(detail);
      toast.error(detail);
    } finally {
      setRunning(false);
    }
  };

  // Prepare chart data: [{t: 0, "V-011": 5.0, "V-012": 4.8, ...}, ...]
  const chartData = React.useMemo(() => {
    if (!result?.history?.length) return [];
    return result.history.map((frame) => {
      const row = { t: Math.round(frame.t) };
      for (const [tag, st] of Object.entries(frame.vessels || {})) {
        row[`${tag}_P`] = st.P_barg;
        row[`${tag}_T`] = st.T_c;
        row[`${tag}_L`] = st.level_m;
      }
      return row;
    });
  }, [result]);

  const vesselTags = React.useMemo(() => {
    if (!result?.history?.length) return [];
    return Object.keys(result.history[0].vessels || {});
  }, [result]);

  const palette = ["#5ac8fa", "#ff9f0a", "#32d74b", "#bf5af2", "#ff453a",
                      "#64d2ff", "#ffd60a", "#30d158", "#ff2d55", "#66d4cf"];

  return (
    <div data-testid="autosim-dynamic-panel" style={{ padding: "8px 4px" }}>
      {/* Controls */}
      <div style={{
        display: "flex", gap: 10, alignItems: "flex-end",
        padding: 12, border: "1px solid var(--pf-border)", borderRadius: 6,
        background: "rgba(90, 200, 250, 0.04)", marginBottom: 12,
      }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>
            <Activity size={11} style={{ display: "inline", marginRight: 4 }} />
            Scenario
          </div>
          <select
            data-testid="dynamic-scenario-select"
            value={scenario}
            onChange={(e) => setScenario(e.target.value)}
            style={{
              width: "100%", padding: "7px 9px", fontSize: 12,
              background: "var(--pf-surface)", color: "var(--pf-text)",
              border: "1px solid var(--pf-border)", borderRadius: 4,
            }}
          >
            {scenarios.map((s) => (
              <option key={s.key} value={s.key}>{s.name}</option>
            ))}
          </select>
        </div>
        <div>
          <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 4 }}>Duration (s)</div>
          <input
            data-testid="dynamic-tend-input"
            type="number"
            value={tEnd}
            min={60} max={86400} step={60}
            onChange={(e) => setTEnd(Number(e.target.value))}
            style={{
              width: 110, padding: "7px 9px", fontSize: 12,
              background: "var(--pf-surface)", color: "var(--pf-text)",
              border: "1px solid var(--pf-border)", borderRadius: 4,
            }}
          />
        </div>
        <button
          data-testid="dynamic-run-btn"
          type="button"
          onClick={run}
          disabled={running}
          style={{
            padding: "8px 14px", fontSize: 12, fontWeight: 500,
            background: running ? "var(--pf-border)" : "var(--pf-spark)",
            color: "#001122", border: 0, borderRadius: 4,
            cursor: running ? "not-allowed" : "pointer",
            display: "flex", alignItems: "center", gap: 6,
          }}
        >
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />}
          {running ? "Running…" : "Run Dynamic"}
        </button>
      </div>

      {err && (
        <div data-testid="dynamic-error" style={{
          padding: 10, borderRadius: 6, fontSize: 12,
          background: "rgba(255, 69, 58, 0.1)", color: "#ff6e6e",
          border: "1px solid rgba(255, 69, 58, 0.35)", marginBottom: 12,
        }}>
          <AlertTriangle size={12} style={{ display: "inline", marginRight: 6 }} />
          {err}
        </div>
      )}

      {/* Analysis summary */}
      {result?.analysis?.worst_case?.tag && (
        <div data-testid="dynamic-analysis" style={{
          padding: 10, borderRadius: 6, fontSize: 12,
          background: "rgba(50, 215, 75, 0.05)",
          border: "1px solid rgba(50, 215, 75, 0.25)", marginBottom: 12,
        }}>
          <CheckCircle2 size={12} style={{ display: "inline", marginRight: 6, color: "#32d74b" }} />
          Worst case: <strong>{result.analysis.worst_case.tag}</strong> — peak
          pressure <strong>{result.analysis.worst_case.peak_P_barg.toFixed(2)} barg</strong>
          {" · "}
          {result.scenario_description}
        </div>
      )}

      {/* Charts */}
      {chartData.length > 0 && (
        <>
          {/* Pressure time-series */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
              Vessel Pressure (barg)
            </div>
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#2a4870" strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke="#cfe7ff" fontSize={10}
                    label={{ value: "Time (s)", position: "insideBottom",
                                offset: -2, fontSize: 10, fill: "#cfe7ff" }} />
                <YAxis stroke="#cfe7ff" fontSize={10} />
                <Tooltip contentStyle={{ background: "#0a1428",
                             border: "1px solid #2a4870", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {vesselTags.map((tag, i) => (
                  <Line key={tag} type="monotone" dataKey={`${tag}_P`}
                      stroke={palette[i % palette.length]} dot={false}
                      name={tag} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Temperature time-series */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, opacity: 0.7, marginBottom: 6 }}>
              Vessel Temperature (°C)
            </div>
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={chartData}>
                <CartesianGrid stroke="#2a4870" strokeDasharray="3 3" />
                <XAxis dataKey="t" stroke="#cfe7ff" fontSize={10} />
                <YAxis stroke="#cfe7ff" fontSize={10} />
                <Tooltip contentStyle={{ background: "#0a1428",
                             border: "1px solid #2a4870", fontSize: 11 }} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                {vesselTags.map((tag, i) => (
                  <Line key={tag} type="monotone" dataKey={`${tag}_T`}
                      stroke={palette[i % palette.length]} dot={false}
                      name={tag} isAnimationActive={false} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>

          {/* Per-vessel analysis table */}
          <div style={{
            border: "1px solid var(--pf-border)", borderRadius: 6,
            overflow: "hidden",
          }}>
            <table data-testid="dynamic-analysis-table" style={{
              width: "100%", fontSize: 11, borderCollapse: "collapse",
            }}>
              <thead style={{ background: "rgba(90, 200, 250, 0.06)" }}>
                <tr>
                  <th style={{ padding: "8px 10px", textAlign: "left" }}>Vessel</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Initial P (barg)</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Peak P (barg)</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Peak Time (s)</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Settling (s)</th>
                  <th style={{ padding: "8px 10px", textAlign: "right" }}>Overshoot (%)</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(result.analysis?.vessels || {}).map(([tag, d]) => (
                  <tr key={tag} style={{ borderTop: "1px solid var(--pf-border)" }}>
                    <td style={{ padding: "7px 10px", fontWeight: 500 }}>{tag}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}>{d.initial_P_barg.toFixed(2)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right", color: "#ff9f0a" }}>{d.peak_P_barg.toFixed(2)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}>{d.peak_time_s.toFixed(0)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}>{d.settling_time_s.toFixed(0)}</td>
                    <td style={{ padding: "7px 10px", textAlign: "right" }}>{d.overshoot_pct.toFixed(1)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {!running && !chartData.length && !err && (
        <div style={{ padding: 32, textAlign: "center", opacity: 0.6, fontSize: 12 }}>
          Select a scenario and click <strong>Run Dynamic</strong> to simulate the plant
          response to operational disturbances.
        </div>
      )}
    </div>
  );
}
